import { ALL_CAMPAIGN_CANDIDATES_QUERY } from "@lib/graphql/queries";
import { useCampaignChannel } from "@lib/hooks/useCampaignChannel";
import { interviewStepBuckets, type ProcessStep, type SelectionStep } from "@lib/selectionSteps";
import { useCallback } from "react";
import { useQuery } from "urql";
import { type Campaign, STAGE_NAMES } from "./context";

interface Props {
  campaign: Campaign;
}

interface HeaderCandidate {
  id: string;
  stage: string;
  candidateSelectionSteps: SelectionStep[];
}

// "Client Interviews" is the 8th macro stage (1-indexed).
const INTERVIEW_STAGE = 8;

export function PipelineFunnelHeader({ campaign }: Props) {
  const stageName = STAGE_NAMES[campaign.currentStage - 1] ?? "—";
  const targetStartDate = campaign.brief?.currentVersion?.targetStartDate ?? null;

  const [{ data }, reexecute] = useQuery({
    query: ALL_CAMPAIGN_CANDIDATES_QUERY,
    variables: { id: campaign.id },
  });
  const refresh = useCallback(() => reexecute({ requestPolicy: "network-only" }), [reexecute]);
  useCampaignChannel(campaign.id, refresh);

  const candidates: HeaderCandidate[] = data?.campaign?.campaignCandidates ?? [];
  const processSteps: ProcessStep[] = data?.campaign?.brief?.selectionProcess?.steps ?? [];
  const hasInterviewCandidates = candidates.some((cc) => cc.stage === "interview");
  const stepBuckets =
    campaign.currentStage === INTERVIEW_STAGE && hasInterviewCandidates
      ? interviewStepBuckets(candidates, processSteps)
      : [];
  const stepSummary = stepBuckets.map((b) => `${b.count} · ${b.label}`).join("  ");

  return (
    <div
      className="overflow-hidden rounded-lg bg-border shadow-sm ring-1 ring-ring"
      data-testid="pipeline-funnel-header"
    >
      <div className="grid grid-cols-2 gap-px sm:grid-cols-4">
        <Tile label="Candidates">
          <span className="text-2xl font-semibold tabular-nums text-text-primary">
            {campaign.candidateCount}
          </span>
        </Tile>
        <Tile label="Current Stage">
          <span className="text-base font-semibold leading-tight text-text-primary">
            {stageName}
          </span>
          {stepSummary && (
            <span className="text-xs text-text-muted" data-testid="funnel-step-summary">
              {stepSummary}
            </span>
          )}
        </Tile>
        <Tile label="Target Start">
          <span className="text-base font-semibold tabular-nums text-text-primary">
            {formatTargetStart(targetStartDate)}
          </span>
        </Tile>
        <Tile label="Days Open">
          <span className="text-2xl font-semibold tabular-nums text-text-primary">
            {daysOpen(campaign.createdAt)}
          </span>
        </Tile>
      </div>
    </div>
  );
}

function Tile({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-1 bg-card px-4 py-3">
      <span className="text-xs font-medium uppercase tracking-wide text-text-muted">{label}</span>
      {children}
    </div>
  );
}

function formatTargetStart(iso: string | null | undefined): string {
  if (!iso) return "—";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function daysOpen(iso: string): number {
  const diff = Date.now() - new Date(iso).getTime();
  return Math.max(0, Math.floor(diff / (1000 * 60 * 60 * 24)));
}
